import { useEffect, useMemo, useState } from 'react';

import { useOpenNotification } from 'utils/antd/notification-toasts';
import { hasValue } from 'utils/hasValue';

import type {
  GetIntegrationBusinessUnitsParams,
  IntegrationBusinessUnit
} from 'api';
import { useGetIntegrationBusinessUnits } from 'api';

import { useOrganisationId } from 'hooks/context';
import { useVirtualList } from 'hooks/useVirtualList';

import {
  BUSINESS_UNITS_BATCH_ERROR_MESSAGE,
  BUSINESS_UNITS_BATCH_ERROR_TOAST_KEY,
  BUSINESS_UNITS_PAGE_SIZE
} from './constants';

type UseBusinessUnitsListArgs = {
  search: string;
  listVersion: number;
};

export const useBusinessUnitsList = ({
  search,
  listVersion
}: UseBusinessUnitsListArgs) => {
  const organisationId = useOrganisationId<number>();
  const openNotification = useOpenNotification();
  const [cursorPrev, setCursorPrev] = useState<string | undefined>();

  const params = useMemo<GetIntegrationBusinessUnitsParams>(() => {
    const assembled: GetIntegrationBusinessUnitsParams = {
      page_size: BUSINESS_UNITS_PAGE_SIZE
    };
    if (search !== '') assembled['filter[name]'] = search;
    if (hasValue(cursorPrev)) assembled.cursor_prev = cursorPrev;
    return assembled;
  }, [search, cursorPrev]);

  const { data, isFetching, isError } = useGetIntegrationBusinessUnits(
    organisationId,
    params
  );

  const cursorNext =
    data?.meta && data.meta.has_next_page && hasValue(data.meta.cursor_next)
      ? String(data.meta.cursor_next)
      : undefined;

  const {
    onScroll,
    data: businessUnits,
    optimisticallyUpdateElement,
    removeElement
  } = useVirtualList<IntegrationBusinessUnit>({
    data: data?.data,
    uniqueKey: 'uuid',
    isFetching,
    updateCursorCallback: setCursorPrev,
    resetConditions: [search, listVersion],
    cursorNext
  });

  const hasLoadedUnits = businessUnits.length > 0;

  useEffect(() => {
    if (isError && hasLoadedUnits) {
      openNotification({
        kind: 'error',
        message: BUSINESS_UNITS_BATCH_ERROR_MESSAGE,
        key: BUSINESS_UNITS_BATCH_ERROR_TOAST_KEY
      });
    }
  }, [isError]);

  return {
    businessUnits,
    onScroll,
    isFetching,
    isError,
    updateElement: optimisticallyUpdateElement,
    removeElement
  };
};
